import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Box, Button, Divider, Grid, Input, Text } from 'theme-ui';
import { Stack } from '@bedrock-layout/stack';
import { Inline } from '@bedrock-layout/inline';
import { Split } from '@bedrock-layout/split';
import { Checkbox, Modal, notification } from 'antd';
import _ from 'lodash';

import SiteNavigation from 'Components/molecules/SiteNavigation';
import BreadCrumbs from 'Components/molecules/Breadcrumbs';
import Section from 'Components/molecules/Section';
import FieldGroup from 'Components/molecules/FieldGroup';
import SelectField from 'Components/molecules/SelectField';
import SwitchField from 'Components/molecules/SwitchField';
import Loader from 'Components/molecules/Loader';
import Tooltip from 'Components/molecules/Tooltip';
import BulkCodeFlagResults from 'Components/molecules/BulkCodeFlagResults';
import useTenants from 'Hooks/useTenants';
import useBulkCodeFlags from 'Hooks/useBulkCodeFlags';
import {
  ACTION_OPTIONS,
  BRANCH_OPTIONS,
  BULK_FORM_INITIAL_STATE,
  CODE_FLAG_ACTIONS,
  TYPE_OPTIONS,
  apiErrorMessage,
  buildBulkRequest,
  collectFlagKeys,
  currentFlagValue,
  describeRequest,
  formatFlagValue,
  isDestructiveRequest,
  tenantsInBranch,
} from 'Utils/bulkCodeFlags';
import FormControls from './FormControls';

const KEY_DATALIST_ID = 'bulk-code-flag-keys';

const BULK_FORM_SCHEMA = yup.object().shape({
  branch: yup.string().oneOf(BRANCH_OPTIONS.map((o) => o.value)).required(),
  action: yup.string().oneOf(Object.values(CODE_FLAG_ACTIONS)).required(),
  key: yup.string().trim().required('Flag name is required'),
  type: yup.string().oneOf(TYPE_OPTIONS.map((o) => o.value)),
  boolValue: yup.bool(),
  stringValue: yup.string().when(['action', 'type'], {
    is: (action, type) => action === CODE_FLAG_ACTIONS.SET && type === 'string',
    then: yup.string().trim().required('Value is required for a string flag'),
  }),
  targetAll: yup.bool(),
  force: yup.bool(),
  identifiers: yup
    .array()
    .of(yup.string())
    .when('targetAll', { is: false, then: yup.array().min(1, 'Select at least one tenant') }),
});

function CodeFlagsBulk() {
  const tenantsQuery = useTenants();
  const bulk = useBulkCodeFlags();
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: BULK_FORM_INITIAL_STATE,
    resolver: yupResolver(BULK_FORM_SCHEMA),
  });
  const { watch, register, control, handleSubmit, setValue, formState } = methods;

  const branch = watch('branch');
  const action = watch('action');
  const type = watch('type');
  const key = watch('key');
  const targetAll = watch('targetAll');
  const identifiers = watch('identifiers') || [];

  const branchTenants = useMemo(() => tenantsInBranch(tenantsQuery.data, branch), [tenantsQuery.data, branch]);
  const knownKeys = useMemo(() => collectFlagKeys(branchTenants), [branchTenants]);
  const keyError = _.get(formState.errors, 'key');
  const stringValueError = _.get(formState.errors, 'stringValue');
  const identifiersError = _.get(formState.errors, 'identifiers');

  useEffect(() => {
    const subscription = watch(() => setPreview(null));
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    setValue('identifiers', []);
  }, [branch, setValue]);

  const confirmRef = useRef(null);

  useEffect(
    () => () => {
      if (confirmRef.current) {
        confirmRef.current.destroy();
      }
    },
    []
  );

  const targetCount = targetAll ? branchTenants.length : identifiers.length;

  const notifyError = (err) =>
    notification.error({ message: 'Bulk code flag update failed', description: apiErrorMessage(err) });

  const runPreview = (values) => {
    setApplied(null);
    bulk.mutate(buildBulkRequest(values, true), {
      onSuccess: (result) => setPreview(result),
      onError: notifyError,
    });
  };

  const runApply = (values) => {
    const request = buildBulkRequest(values, false);
    const destructive = isDestructiveRequest(request);

    confirmRef.current = Modal.confirm({
      title: destructive ? 'Apply to tenants? This is a wide change.' : 'Apply to tenants?',
      content: describeRequest(request, targetCount),
      okText: 'Apply',
      okButtonProps: { danger: destructive },
      onOk: () =>
        bulk
          .mutateAsync(request)
          .then((result) => {
            setApplied(result);
            setPreview(null);
            tenantsQuery.refetch();
            notification[result.summary.failed > 0 ? 'warning' : 'success']({
              message: result.summary.failed > 0 ? 'Applied with failures' : 'Successfully applied',
              description: `Changed ${result.summary.succeeded}, skipped ${result.summary.skipped}, failed ${result.summary.failed}.`,
            });
          })
          .catch(notifyError),
    });
  };

  if (tenantsQuery.isLoading) return <Loader isActive={true} />;

  return (
    <FormProvider {...methods}>
      <Loader isActive={bulk.isLoading} />
      <Grid variant="layout" bg="muted">
        <Grid variant="layout.header" bg="background" gap={0}>
          <SiteNavigation />
          <Divider variant="layout.header.divider" color="lightgray" />
          <BreadCrumbs />
          <FormControls />
        </Grid>
        <Box as="form" variant="layout.body" bg="background" pt="4" pb="6" px="5" onSubmit={(e) => e.preventDefault()}>
          <Stack gutter="lgXl">
            <Section>
              <Text variant="section">
                Flag change
                <Tooltip text="Set creates the flag where missing and updates it where present. Delete removes it. Tenants already matching are skipped." />
              </Text>
              <Split gutter="md" fraction="2/3">
                <FieldGroup>
                  <SelectField label="Environment (branch)" name="branch" options={BRANCH_OPTIONS} />
                  <SelectField label="Action" name="action" options={ACTION_OPTIONS} />
                  <Stack gutter="lg">
                    <Text variant="label">Flag name</Text>
                    <Input
                      list={KEY_DATALIST_ID}
                      placeholder="e.g. stageInvitation"
                      variant={keyError ? 'input.error' : 'input'}
                      autoComplete="off"
                      {...register('key')}
                    />
                    <datalist id={KEY_DATALIST_ID}>
                      {knownKeys.map((knownKey) => (
                        <option key={knownKey} value={knownKey} />
                      ))}
                    </datalist>
                    {keyError && <Text color="errorText">{keyError.message}</Text>}
                  </Stack>
                  {action === CODE_FLAG_ACTIONS.SET && (
                    <>
                      <SelectField label="Type" name="type" options={TYPE_OPTIONS} />
                      {type === 'string' ? (
                        <Stack gutter="lg">
                          <Text variant="label">Value</Text>
                          <Input
                            placeholder="Value"
                            variant={stringValueError ? 'input.error' : 'input'}
                            {...register('stringValue')}
                          />
                          {stringValueError && <Text color="errorText">{stringValueError.message}</Text>}
                        </Stack>
                      ) : (
                        <Stack gutter="lg">
                          <Text variant="label">Value</Text>
                          <Inline gutter="md" align="center">
                            <SwitchField label="" name="boolValue" />
                            <Text>{watch('boolValue') ? 'true' : 'false'}</Text>
                          </Inline>
                        </Stack>
                      )}
                    </>
                  )}
                </FieldGroup>
              </Split>
            </Section>
            <Divider color="divider" />
            <Section>
              <Text variant="section">
                Target tenants
                <Tooltip text="All tenants in the selected environment, or pick a subset." />
              </Text>
              <Stack gutter="lg">
                <Inline gutter="md" align="center">
                  <SwitchField label="" name="targetAll" />
                  <Text>
                    All tenants in {branch} ({branchTenants.length})
                  </Text>
                </Inline>
                <Inline gutter="md" align="center">
                  <SwitchField label="" name="force" />
                  <Text>Republish tenants that already match (re-upload config and revalidate)</Text>
                </Inline>
                {!targetAll && (
                  <Stack gutter="md">
                    <Controller
                      name="identifiers"
                      control={control}
                      render={({ field }) => (
                        <Checkbox.Group value={field.value || []} onChange={field.onChange}>
                          <Stack gutter="sm">
                            {branchTenants.map((tenant) => (
                              <Checkbox key={tenant.identifier} value={tenant.identifier}>
                                {tenant.name}{' '}
                                <Text as="span" sx={{ color: 'disabledText' }}>
                                  ({tenant.identifier}
                                  {key ? `, current: ${formatFlagValue(currentFlagValue(tenant, key))}` : ''})
                                </Text>
                              </Checkbox>
                            ))}
                          </Stack>
                        </Checkbox.Group>
                      )}
                    />
                    {branchTenants.length === 0 && (
                      <Text sx={{ color: 'disabledText', fontStyle: 'italic' }}>No tenants in {branch}.</Text>
                    )}
                    {identifiersError && <Text color="errorText">{identifiersError.message}</Text>}
                  </Stack>
                )}
              </Stack>
            </Section>
            <Divider color="divider" />
            <Section>
              <Inline gutter="md" align="center">
                <Button
                  type="button"
                  variant="primary.outlined"
                  disabled={bulk.isLoading || targetCount === 0}
                  onClick={handleSubmit(runPreview)}
                >
                  Preview (dry run)
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  disabled={bulk.isLoading || !preview}
                  onClick={handleSubmit(runApply)}
                >
                  Apply
                </Button>
                {!preview && !applied && (
                  <Text sx={{ color: 'disabledText' }}>Run a preview before applying.</Text>
                )}
              </Inline>
              <BulkCodeFlagResults title="Preview" response={preview} />
              <BulkCodeFlagResults title="Result" response={applied} />
            </Section>
          </Stack>
        </Box>
      </Grid>
    </FormProvider>
  );
}

export default CodeFlagsBulk;
